/**
 * Página inicial — frases curtas, escaneáveis no mobile.
 */

export const META_SITE = {
  title: 'Lunins — Site oficial | Loja e bem-estar institucional',
  description:
    'Projetos de bem-estar institucional, salas de descompressão e loja oficial de equipamentos Lunins. Mato Grosso do Sul — L.D.Z Arquitetura Bem Estar LTDA.',
} as const;

export const HERO_EXPANDIDO = {
  eyebrow: 'Projetos institucionais · Loja oficial · MS',
  headline: 'A experiência de bem-estar que a Lunins leva às instituições também pode chegar à sua rotina.',
  subBefore: 'Em ',
  subLinkLabel: 'lunins.org',
  subLinkHref: 'https://www.lunins.org',
  subAfter:
    ', você encontra o contexto institucional. Aqui, a loja oficial reúne equipamentos da mesma curadoria, com preço de referência e fechamento humano pela equipe.',
  ctaLoja: 'Ver equipamentos e preços',
  ctaInstitucional: 'Ver institucional',
  ctaInstitucionalHref: 'https://www.lunins.org',
  ctaComoComprar: 'Como comprar',
  ctaComoComprarHash: '#comprar',
} as const;

export const COMO_COMPRAR = {
  id: 'comprar',
  title: 'Como a compra acontece',
  leadBefore: 'Loja oficial Lunins: catálogo aberto com preço de referência. Contexto institucional em ',
  leadLinkLabel: 'lunins.org',
  leadLinkHref: 'https://www.lunins.org',
  leadAfter: '; valor final, prazo e entrega seguem pelo WhatsApp ou pelo link enviado pela equipe.',
  passos: [
    {
      titulo: '1. Escolha',
      texto: 'Compare equipamentos na vitrine e entenda qual linha faz sentido para a sua rotina.',
    },
    {
      titulo: '2. Confirmação',
      texto: 'Quando houver campanha, proposta ou consultor, as condições comerciais chegam pelo canal oficial.',
    },
    {
      titulo: '3. Pagamento',
      texto: 'PIX ou cartão conforme combinado, sempre com orientação Lunins e identificação da marca.',
    },
  ],
  notaLegal: 'Pagamento sensível só pelo link ou orientação que a Lunins enviar; frete e prazo dependem de região e campanha.',
} as const;

export const SALAS_DESCOMPRESSAO = {
  id: 'salas',
  title: 'Salas de descompressão',
  lead:
    'A Lunins estrutura pausas guiadas com equipamento, orientação e rotina de uso. Não é um aparelho solto no canto: é uma solução pensada para reduzir desgaste no dia a dia de órgãos e empresas.',
  listaTitulo: 'Em geral inclui',
  lista: [
    'Diagnóstico: espaço, fluxo e perfil da equipe.',
    'Implantação: equipamentos por categoria, treino de uso e padrão operacional.',
    'Acompanhamento: ajustes, relatórios e registro alinhado a processos da gestão.',
  ],
} as const;

/** Fluxo institucional — reforça lastro operacional sem duplicar o teaser de salas */
export const PROCESSO_INSTITUCIONAL = {
  id: 'processo',
  title: 'Como a Lunins atua nas instituições',
  lead: 'A lógica é simples: entender o contexto, implantar com critério e acompanhar para manter o padrão.',
  passos: [
    {
      titulo: 'Diagnóstico',
      texto: 'Entendimento do espaço, da rotina e do que a gestão precisa medir ou comunicar.',
    },
    {
      titulo: 'Implantação',
      texto: 'Montagem da sala, curadoria de equipamentos e capacitação para uso seguro.',
    },
    {
      titulo: 'Acompanhamento',
      texto: 'Revisões, ajustes e documentação para manter o padrão ao longo do tempo.',
    },
  ],
} as const;

export const PARA_ORGANIZACOES = {
  id: 'organizacoes',
  title: 'Para instituições, RH e compras',
  intro:
    'Projetos com etapas claras, responsabilidade operacional e linguagem compatível com gestão, compras e RH.',
  blocos: [
    {
      titulo: 'Operação em campo',
      texto: 'Equipe que instala, treina e revisa — não apenas fornecimento de equipamento.',
    },
    {
      titulo: 'Sob medida',
      texto: 'Piloto, expansão ou sala fixa conforme orçamento, espaço e cronograma.',
    },
    {
      titulo: 'Loja oficial',
      texto: 'Mesma marca: o que a equipe recomenda em instituições reflete na vitrine comercial.',
    },
  ],
  cta: 'Fale com a equipe',
  ctaHref: 'mailto:comunicacao@lunins.org',
  ctaSec: 'lunins.org',
  ctaSecHref: 'https://www.lunins.org',
} as const;

export const PARA_COMPRADORES = {
  id: 'compradores',
  title: 'Para quem compra na loja',
  intro:
    'Aqui está a vitrine comercial oficial da Lunins: preços para comparação, atendimento humano e a mesma identidade de marca que aparece no institucional.',
  topicos: [
    {
      titulo: 'Curadoria',
      texto: 'Linha que usamos em campo: pés, costas, shiatsu, vibro, cintas, manuais.',
    },
    {
      titulo: 'Campanhas',
      texto: 'Proposta do consultor pode ter condições diferentes da loja aberta.',
    },
    {
      titulo: 'Depois da compra',
      texto: 'Suporte Lunins e garantia conforme fabricante.',
    },
  ],
} as const;

export const TUTORIAIS_IG = {
  id: 'tutoriais',
  eyebrow: 'Instagram',
  title: 'Uso orientado pela própria marca',
  lead: '@luninsbr reúne orientações por família de aparelho e reforça continuidade da comunicação oficial.',
  destaques: [
    {
      titulo: 'Reels',
      texto: 'Busque por equipamento ou categoria.',
      href: 'https://www.instagram.com/luninsbr/reels/',
    },
    {
      titulo: 'Perfil',
      texto: 'Campanhas e comunicação oficial.',
      href: 'https://www.instagram.com/luninsbr/',
    },
  ],
} as const;

export const DEPOIMENTOS = {
  id: 'depoimentos',
  title: 'Sinais do campo',
  eyebrow: 'Tom de campo — não métrica auditável',
  lead:
    'Relatos anônimos sobre percepção de uso e clima em contexto institucional. Servem como indício de experiência, não como prova estatística.',
  items: [
    {
      quote: 'A fila na sala virou pausa que a gente respeita.',
      nome: 'Servidora pública',
      org: 'MS',
    },
    {
      quote: 'O clima melhorou na conversa de corredor.',
      nome: 'Coordenação',
      org: 'Ensino',
    },
    {
      quote: 'Levei para casa o mesmo equipamento do trabalho.',
      nome: 'Cliente',
      org: 'Campo Grande — MS',
    },
  ],
} as const;

export const CATALOGO_TEASER = {
  title: 'Equipamentos em destaque',
  subtitle: 'A loja oficial existe para isso: comparar a linha Lunins com clareza antes de falar com a equipe.',
  botao: 'Ver catálogo completo',
} as const;
